import React from "react";
import * as M from "../../../Modal";
import * as BS from "react-bootstrap";
import * as H from "../../../../hooks";
import * as C from "../../../../Common";
import * as S from "../../../../services";
import { T, TC } from "../../../../Constants";

type Map = {
    /** The BTV user */
    user: string;
    /** The BTV password */
    password: string;
    /** The mapping of each site to the BTV equivalent */
    sites: (Record<"aiset_id", string> & Record<"btv_id", string[]>)[];
}

export type SiteMap = Map;
type Sites = Awaited<ReturnType<typeof S.getContextSites>>["data"];
type Errors = T.Errors<Omit<T.DataSource, "element_mapping"> & T.DataSource["credentials"]> & Partial<Record<"element_mapping", string[]>>;

export type InstallationMapperProps = {
    /** The element linked to the datasource */
    context: T.ContextParams;
    /** The current datasource */
    datasource: T.DataSource;
    /** Callback to update the datasource */
    update: React.Dispatch<React.SetStateAction<T.DataSource>>;
}

export type InstallationMapperRef = {
    /** Check errors */
    has_errors: () => boolean;
}

const InstallationMapper = React.forwardRef<InstallationMapperRef, InstallationMapperProps>(({ update, ...props }, ref) => {
    const [errors, set_errors] = React.useState<Errors>({});
    const user_ref = React.useRef<C.Form.TextFieldRef>(null);
    const password_ref = React.useRef<C.Form.PasswordRef>(null);
    const [sites, set_sites, status] = H.useAsyncState<Sites>([]);
    const [btv_sites, set_btv_sites, status_btv_sites] = H.useAsyncState<Sites, "unrequested">([], "unrequested");

    const check_errors = React.useCallback(() => {
        let new_errors: Errors = {};
        const password = props.datasource.credentials?.password || password_ref.current?.get_controlled?.();
        const user = props.datasource.credentials?.user || user_ref.current?.single?.current?.get_controlled_single?.();

        if (!user) new_errors.user = TC.GLOBAL_REQUIRED_FIELD;
        if (!password) new_errors.password = TC.GLOBAL_REQUIRED_FIELD;

        const site_erros = [] as Errors["element_mapping"];
        (props.datasource.element_mapping || []).forEach((site, index) => {
            if (site.external_ids.length === 0) site_erros[index] = TC.GLOBAL_REQUIRED_FIELD;
        });
        // If there was some errors for the sites, store them
        if (site_erros.some(x => typeof x === "string")) new_errors.element_mapping = site_erros;
        // Is there any error ?
        const has_errors = Object.keys(new_errors).length > 0;
        if (has_errors) set_errors(new_errors);
        return has_errors;
    }, [props.datasource.credentials, props.datasource.element_mapping]);

    React.useImperativeHandle(ref, () => ({ has_errors: check_errors }), [check_errors]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getContextSites(props.context)
            .then(res => {
                if (isSubscribed) {
                    const new_sites_ids = res.data.map(x => x.value);
                    // Save the elements sites
                    set_sites(res.data, "done");
                    // Update the mapping, add the sites not configured yet,
                    // and remove the sites that are no longer available
                    update(p => {
                        const new_sites: typeof p.element_mapping = [];
                        // Remove the sites that are no longer available
                        for (const map of (p.element_mapping || [])) {
                            if (new_sites_ids.includes(map.site_id)) new_sites.push(map);
                        }
                        // Add the missing sites
                        for (const id of new_sites_ids) {
                            if (!(p.element_mapping || []).some(x => x.site_id === id)) new_sites.push({ site_id: id, external_ids: [], items: [] });
                        }
                        return { ...p, element_mapping: new_sites };
                    });
                }
            })
            .catch(() => isSubscribed && set_sites([], "error"))
        return () => {
            isSubscribed = false;
            set_sites([], "load");
        }
    }, [set_sites, update, props.context]);

    const mapped_sites = React.useMemo(() => {
        return sites.map(site => {
            const mapped_site = props.datasource.element_mapping.find(x => x.site_id === site.value);
            return { ...site, btv_id: mapped_site?.external_ids || [] };
        });
    }, [props.datasource.element_mapping, sites]);

    const fetch_btv_sites = React.useCallback(() => {
        let new_errors: Errors = {};
        let password = props.datasource.credentials?.password;
        let user = props.datasource.credentials?.user;
        // For update of the credentials if needed
        if (!password || !user) {
            if (!password) password = password_ref.current?.get_controlled?.();
            if (!user) user = user_ref.current?.single?.current?.get_controlled_single?.();
            update(p => ({ ...p, credentials: { ...p.credentials, password, user } }));
        }

        // Check if the user and password are valid
        if (!user) new_errors.user = TC.GLOBAL_REQUIRED_FIELD;
        if (!password) new_errors.password = TC.GLOBAL_REQUIRED_FIELD;
        // If there was some errors, store them
        if (Object.keys(new_errors).length > 0) set_errors(new_errors);
        else {
            // Set the state as loading
            set_btv_sites([], "load");
            // Fetch the data
            S.loadBTVSites({ user, password })
                .then(res => set_btv_sites(res.data, "done"))
                .catch(error => {
                    M.Alerts.loadError(error);
                    set_btv_sites([], "unrequested");
                });
        }
    }, [props.datasource.credentials, update]);

    //#region Errors
    React.useEffect(() => {
        if (props.datasource.credentials?.user) set_errors(p => ({ ...p, user: undefined }));
    }, [props.datasource.credentials]);

    React.useEffect(() => {
        if (props.datasource.credentials?.password) set_errors(p => ({ ...p, password: undefined }));
    }, [props.datasource.credentials]);

    React.useEffect(() => {
        const new_errors: Errors["element_mapping"] = [];
        if (Array.isArray(props.datasource.element_mapping)) props.datasource.element_mapping.forEach((site, index) => {
            if (site.external_ids.length === 0) new_errors[index] = TC.GLOBAL_REQUIRED_FIELD;
        });
        if (new_errors.some(x => typeof x === "string")) set_errors(p => ({ ...p, element_mapping: new_errors }));
        else set_errors(p => ({ ...p, element_mapping: undefined }));
    }, [props.datasource.element_mapping]);
    //#endregion

    return <>
        <C.Title level={4} text={TC.DATASOURCE_SITE_MAPPING} />

        <C.Spinner status={status}>
            <BS.Row className="g-1 mb-3">
                <BS.Col>
                    <C.Form.TextField
                        ref={user_ref}
                        noBottomMargin
                        autocomplete="off"
                        error={errors.user}
                        name="btv_credentials"
                        label={TC.DATASOURCE_USER_NAME}
                        value={props.datasource.credentials?.user}
                        onChange={user => update(p => ({ ...p, credentials: { ...p.credentials, user } }))}
                    />
                </BS.Col>

                <BS.Col>
                    <C.Form.Password
                        noBottomMargin
                        autocomplete="off"
                        ref={password_ref}
                        name="btv_credentials"
                        error={errors.password}
                        label={TC.DATASOURCE_PASSWORD}
                        value={props.datasource.credentials?.password}
                        onChange={password => update(p => ({ ...p, credentials: { ...p.credentials, password } }))}
                    />
                </BS.Col>

                <BS.Col md={1} className="d-flex align-items-end">
                    <C.Button
                        variant="info"
                        className="w-100"
                        onClick={fetch_btv_sites}
                        tip={TC.DATASOURCE_TEST_CREDENTIALS}
                        icon={{ icon: "refresh", spinIcon: "refresh", spin: status_btv_sites === "load" }}
                    />
                </BS.Col>
            </BS.Row>

            {mapped_sites.map((site, index) => <C.Form.Select
                multiple
                key={site.value}
                label={site.label}
                options={btv_sites}
                value={site.btv_id}
                loading={status_btv_sites === "load"}
                disabled={status_btv_sites !== "done"}
                error={errors.element_mapping?.[index]}
                onChange={(ids: string[]) => update(p => ({ ...p, element_mapping: p.element_mapping.map(x => x.site_id === site.value ? { ...x, external_ids: ids } : x) }))}
            />)}
        </C.Spinner>
    </>;
});

export default InstallationMapper;